import type { Diff } from '@manudev/model';
import * as THREE from 'three';
import { STORE_LAYER } from '@/hero/StoreLayer';
import { elementBox } from '@/render/batch';
import type { ElementEntry, ElementState, LayerRig } from '@/render/rig';
import { useSelectionStore } from '@/state/selection';
import { useHeroStore } from '@/store/store';
import { layersOf, resetLayers, type Frame, type StationImpl } from '../types';
import { ComparePanel } from './Panel';
import { CutSheet } from './Scene';
import { useCompareStore, type DiffKind, type DiffRow } from './state';
import { clearSwipe, measure, swipeTo, type Removed } from './swipe';

const STATE: Record<DiffKind, ElementState> = {
  added: 'pass',
  removed: 'fail',
  changed: 'highlight',
};
const KIND_ORDER: DiffKind[] = ['added', 'removed', 'changed'];
const GHOST = 0.85;

let applied: { diff: Diff | null; store: LayerRig | null; size: number } | null = null;
let removed: Removed[] = [];

export const compare: StationImpl = {
  pick: 'all',
  enter({ layers, height }) {
    resetLayers(layers, height);
    for (const h of layers.values()) {
      if (h.model.discipline === 'mep') h.rig.setVisible(false);
      else if (h.model.discipline === 'space') h.rig.setVisible(true);
      else h.rig.setGhost(GHOST);
    }
    applied = null;
    void useCompareStore.getState().refresh();
  },
  update(frame) {
    const { diff, status, swipe } = useCompareStore.getState();
    const store = frame.layers.get(STORE_LAYER)?.rig ?? null;
    const size = store?.elements.size ?? 0;
    if (!applied || applied.diff !== diff || applied.store !== store || applied.size !== size) {
      apply(frame, diff);
      applied = { diff, store, size };
    }
    if (status === 'ready') swipeTo(frame, swipe, removed);
  },
  leave({ layers, height }) {
    resetLayers(layers, height);
    for (const h of layers.values()) h.rig.clearVisibility();
    layers.get(STORE_LAYER)?.rig.setClipPlane(null);
    clearSwipe();
    removed = [];
    applied = null;
    useSelectionStore.getState().setFocus(null);
  },
  Panel: ComparePanel,
  Scene: CutSheet,
};

// Colours every element the diff names on whichever layer draws it, hides the rooms it does not
// name, and hands the panel its rows.
function apply({ layers }: Frame, diff: Diff | null) {
  const shown = [...layers.values()].filter((h) => h.model.discipline !== 'mep');
  for (const h of shown) {
    h.rig.clearStates();
    h.rig.clearVisibility();
  }
  removed = [];

  const byGlobalId = new Map<string, { rig: LayerRig; el: ElementEntry; layer: string }>();
  for (const h of shown)
    for (const el of h.rig.elements.values())
      if (el.globalId) byGlobalId.set(el.globalId, { rig: h.rig, el, layer: h.model.id });
  const after = new Map(useHeroStore.getState().elements.map((e) => [e.globalId, e]));

  const entries: [DiffKind, string, string[]][] = diff
    ? [
        ...diff.added.map((g): [DiffKind, string, string[]] => ['added', g, []]),
        ...diff.removed.map((g): [DiffKind, string, string[]] => ['removed', g, []]),
        ...diff.changed.map((c): [DiffKind, string, string[]] => ['changed', c.globalId, c.fields]),
      ]
    : [];
  const named = new Set<string>();
  const rows: DiffRow[] = [];
  const box = new THREE.Box3();
  const center = new THREE.Vector3();
  for (const [kind, globalId, fields] of entries) {
    named.add(globalId);
    const hit = byGlobalId.get(globalId);
    const info = after.get(globalId);
    let target: DiffRow['target'] = null;
    if (hit) {
      hit.rig.setElementState(hit.el.expressID, STATE[kind]);
      if (!elementBox(hit.el, box).isEmpty()) {
        box.getCenter(center);
        target = {
          layer: hit.layer,
          expressID: hit.el.expressID,
          center: [center.x, center.y, center.z],
          radius: Math.max(1, box.getSize(new THREE.Vector3()).length() / 2),
        };
        if (kind === 'removed')
          removed.push({
            rig: hit.rig,
            expressID: hit.el.expressID,
            center: center.clone(),
            shown: null,
          });
      }
    }
    rows.push({
      kind,
      globalId,
      type: hit?.el.type || info?.type || '–',
      name: hit?.el.name || info?.name || '–',
      fields,
      target,
    });
  }
  for (const h of layersOf(layers, (d) => d === 'space'))
    for (const el of h.rig.elements.values())
      if (!named.has(el.globalId)) h.rig.setElementVisible(el.expressID, false);

  rows.sort(
    (a, b) =>
      KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) ||
      a.type.localeCompare(b.type) ||
      a.name.localeCompare(b.name),
  );
  measure(shown.map((h) => h.rig));
  useCompareStore.getState().setRows(rows);
}
